import React, { PropsWithChildren, ReactNode } from 'react'
import { Button, Switch, Tooltip } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { getClassNames } from '../../../_utils/classnames'
import { InfoTooltip } from '../../info-tooltip/info-tooltip'

type CtaRowToggle = {
    checked: boolean
    onChange: (checked: boolean) => void
    disabled?: boolean
    /** Shown on hover when the switch is disabled, to say why. */
    tooltip?: ReactNode
}

type CtaRowRemove = {
    onRemove: () => void
    disabled?: boolean
    /** Shown on hover when removal is blocked, to say why. */
    tooltip?: ReactNode
    'aria-label': string
}

type AppMessageCtaRowProps = {
    label: ReactNode
    /** Resolved action, e.g. "Open URL · app://recipes". */
    summary?: ReactNode
    /** Short role marker — Primary, Secondary, Dismiss. */
    tag?: string
    /** Explanatory copy for the row's label. */
    info?: ReactNode
    foregroundColor?: string | null
    backgroundColor?: string | null
    /** Drawn inside the swatch in place of a letter, for the close action. */
    swatchIcon?: ReactNode
    /**
     * A button that exists is removed, not switched off — the row disappears and
     * the add control comes back. Rows that stand for a fixed template property
     * take a `toggle` instead.
     */
    remove?: CtaRowRemove
    toggle?: CtaRowToggle
    expanded?: boolean
    onExpandedChange?: (expanded: boolean) => void
    className?: string
}

/**
 * One button in the message, as a row rather than a nested form — so the
 * buttons can be read down the list and compared against each other, which a
 * pair of separately-collapsed panels made impossible.
 *
 * A collapsed row is a settled summary and recedes; opening it for editing
 * brings it forward. That is the only state the row's surface reports, which is
 * why removal is an explicit control rather than a switch: a row is here or it
 * is gone.
 */
export const AppMessageCtaRow = ({
    label,
    summary,
    tag,
    info,
    foregroundColor,
    backgroundColor,
    swatchIcon,
    remove,
    toggle,
    expanded,
    onExpandedChange,
    className,
    children,
}: PropsWithChildren<AppMessageCtaRowProps>) => {
    const isOn = !toggle || toggle.checked
    const isExpanded = !!expanded && isOn && !!children

    const removeButton = !!remove && (
        <Button
            className="cta-row-remove"
            size="small"
            disabled={remove.disabled}
            icon={<CloseOutlined />}
            aria-label={remove['aria-label']}
            onClick={remove.onRemove}
        />
    )

    const toggleSwitch = !!toggle && (
        <Switch
            size="small"
            checked={toggle.checked}
            disabled={toggle.disabled}
            onChange={(checked) => toggle.onChange(checked)}
            aria-label={typeof label === 'string' ? label : undefined}
        />
    )

    return (
        <div
            className={getClassNames('app-message-cta-row', className, {
                expanded: isExpanded,
                off: !isOn,
            })}
        >
            <div className="cta-row-head">
                <span
                    className="cta-row-swatch"
                    aria-hidden="true"
                    style={{
                        backgroundColor: backgroundColor ?? undefined,
                        color: foregroundColor ?? undefined,
                    }}
                >
                    {swatchIcon ?? 'A'}
                </span>

                <span className="cta-row-identity">
                    <span className="cta-row-label">
                        {label}
                        {!!info && <InfoTooltip title={info} about={typeof label === 'string' ? label : undefined} />}
                    </span>
                    {!!summary && <span className="cta-row-summary">{summary}</span>}
                </span>

                {!!tag && <span className="cta-row-tag">{tag}</span>}

                {!!onExpandedChange && isOn && !!children && (
                    <Button
                        className="cta-row-edit"
                        size="small"
                        aria-expanded={isExpanded}
                        onClick={() => onExpandedChange(!expanded)}
                    >
                        {isExpanded ? 'Done' : 'Edit'}
                    </Button>
                )}

                {!!remove && (
                    <Tooltip title={remove.tooltip ?? 'Remove button'}>
                        {/* A disabled button fires no pointer events, so a
                            blocked removal needs a wrapper for the tooltip to
                            attach to. An enabled one takes it directly, which
                            keeps the tooltip aligned to the button itself. */}
                        {remove.disabled ? <span className="cta-row-remove-wrap">{removeButton}</span> : removeButton}
                    </Tooltip>
                )}

                {!!toggle && (toggle.tooltip ? <Tooltip title={toggle.tooltip}>{toggleSwitch}</Tooltip> : toggleSwitch)}
            </div>

            {isExpanded && <div className="cta-row-body">{children}</div>}
        </div>
    )
}
